import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress, 
  Chip, 
  Grid,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  CloudUpload, 
  CheckCircle, 
  Error, 
  Security,
  Storage,
  Settings,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';

interface SharePointConfig {
  site_url: string;
  document_library: string;
  folder_path?: string;
  client_id: string;
  client_secret?: string;
  tenant_id?: string;
  access_token?: string;
  refresh_token?: string;
  is_configured: boolean;
}

const StorageConfig: React.FC = () => {
  const { supabase } = useSupabase();
  const [config, setConfig] = useState<SharePointConfig>({
    site_url: '',
    document_library: '',
    folder_path: '',
    client_id: '',
    client_secret: '',
    tenant_id: '',
    access_token: '',
    refresh_token: '',
    is_configured: false
  });
  
  const [, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [wizardConfig, setWizardConfig] = useState<SharePointConfig>({
    site_url: '',
    document_library: '',
    folder_path: '',
    client_id: '',
    client_secret: '',
    tenant_id: '',
    access_token: '',
    refresh_token: '',
    is_configured: false
  });

  const loadConfiguration = useCallback(async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('storage_config')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading configuration:', error);
        return;
      }

      if (data) {
        // Don't load sensitive data - require re-entry for security
        const safeConfig = {
          ...data,
          client_secret: '', // Never load client secret
          access_token: '', // Never load access token
          refresh_token: '' // Never load refresh token
        };
        setConfig(safeConfig);
        setWizardConfig(safeConfig);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    }
  }, [supabase]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const saveConfiguration = async (configToSave: SharePointConfig = config) => {
    setLoading(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const { error } = await (supabase as any)
        .from('storage_config')
        .upsert({
          site_url: configToSave.site_url,
          document_library: configToSave.document_library,
          folder_path: configToSave.folder_path,
          client_id: configToSave.client_id,
          client_secret: configToSave.client_secret,
          tenant_id: configToSave.tenant_id,
          access_token: configToSave.access_token,
          refresh_token: configToSave.refresh_token,
          is_configured: configToSave.is_configured
        });

      if (error) {
        throw error;
      }

      setConfig(configToSave);
      setSaveSuccess(true);
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveError('Error saving configuration: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('test-sharepoint-connection', {
        body: { config: wizardConfig }
      });

      if (error) {
        throw error;
      }

      setTestResult({
        success: data.success,
        message: data.message || (data.success ? 'Connection successful!' : 'Connection failed')
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Error testing connection: ' + (error as Error).message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleWizardInputChange = (field: keyof SharePointConfig, value: string) => {
    setWizardConfig(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear test result when configuration changes
    setTestResult(null);
  };



  const steps = [
    'Site Configuration',
    'Authentication Setup',
    'Test & Verify'
  ];

  const isWizardStepComplete = (step: number) => {
    switch (step) {
      case 0:
        return !!(wizardConfig.site_url && wizardConfig.document_library);
      case 1:
        return !!(wizardConfig.client_id && wizardConfig.client_secret);
      case 2:
        return testResult?.success === true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleWizardComplete = async () => {
    // Only complete if test was successful
    if (testResult?.success) {
      const finalConfig = { ...wizardConfig, is_configured: true };
      await saveConfiguration(finalConfig);
      setShowWizard(false);
      setActiveStep(0);
    }
  };

  const startWizard = () => {
    setWizardConfig(config);
    setShowWizard(true);
    setActiveStep(0);
  };

  const renderWizardStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Configure SharePoint Site
            </Typography>
            
            <TextField
              fullWidth
              label="SharePoint Site URL"
              value={wizardConfig.site_url}
              onChange={(e) => handleWizardInputChange('site_url', e.target.value)}
              placeholder="https://yourtenant.sharepoint.com/sites/yoursite"
              required
              sx={{ mb: 3 }}
              helperText="Full URL to your SharePoint site"
            />
            
            <TextField
              fullWidth
              label="Document Library Name"
              value={wizardConfig.document_library}
              onChange={(e) => handleWizardInputChange('document_library', e.target.value)}
              placeholder="Documents"
              required
              sx={{ mb: 3 }}
              helperText="Name of the document library where PDFs will be stored"
            />
            
            <TextField
              fullWidth
              label="Default Folder Path"
              value={wizardConfig.folder_path || ''}
              onChange={(e) => handleWizardInputChange('folder_path', e.target.value)}
              placeholder="ConsentForms/2024"
              sx={{ mb: 3 }}
              helperText="Optional: Default folder path within the document library"
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Configure Authentication
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                You'll need to create an Azure App Registration and get the Client ID and Client Secret.
                These credentials will be securely stored and encrypted.
                {config.is_configured && (
                  <><br/><br/><strong>Note:</strong> Since you're editing an existing configuration, you'll need to re-enter your Client Secret for security reasons.</>
                )}
              </Typography>
            </Alert>
            
            <TextField
              fullWidth
              label="Client ID"
              value={wizardConfig.client_id}
              onChange={(e) => handleWizardInputChange('client_id', e.target.value)}
              placeholder="Your Azure App Registration Client ID"
              required
              sx={{ mb: 3 }}
              helperText="Azure App Registration Client ID"
            />
            
            <TextField
              fullWidth
              label="Client Secret"
              type={showSecrets ? 'text' : 'password'}
              value={wizardConfig.client_secret || ''}
              onChange={(e) => handleWizardInputChange('client_secret', e.target.value)}
              placeholder="Your Azure App Registration Client Secret"
              required
              sx={{ mb: 3 }}
              helperText="Azure App Registration Client Secret"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowSecrets(!showSecrets)}
                    edge="end"
                  >
                    {showSecrets ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
            
            <TextField
              fullWidth
              label="Tenant ID"
              value={wizardConfig.tenant_id || ''}
              onChange={(e) => handleWizardInputChange('tenant_id', e.target.value)}
              placeholder="Your Azure Tenant ID"
              sx={{ mb: 3 }}
              helperText="Azure Tenant ID (optional)"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Test & Verify Configuration
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Make sure your Azure App Registration has the correct permissions 
                (Sites.ReadWrite.All) and that admin consent has been granted.
              </Typography>
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={testing ? <CircularProgress size={20} /> : <CheckCircle />}
                onClick={testConnection}
                disabled={testing || !wizardConfig.site_url || !wizardConfig.document_library}
                sx={{ minWidth: 160 }}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
            </Box>

            {testResult && (
              <Alert 
                severity={testResult.success ? 'success' : 'error'} 
                sx={{ mb: 3 }}
                icon={testResult.success ? <CheckCircle /> : <Error />}
              >
                {testResult.message}
                {!testResult.success && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Troubleshooting:</strong>
                    </Typography>
                    <Typography variant="body2" component="div">
                      • Verify your Azure App Registration has <code>Sites.ReadWrite.All</code> permission<br/>
                      • Ensure admin consent has been granted<br/>
                      • Check that your SharePoint site URL is correct<br/>
                      • Confirm the document library name exists
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {testResult?.success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Configuration Complete!</strong> Your SharePoint connection is working correctly. 
                  You can now complete the setup.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // If not configured, show the wizard
  if (!config.is_configured || showWizard) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Storage sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              SharePoint Setup Wizard
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Follow these steps to configure SharePoint document library integration
          </Typography>
        </Box>

        {/* Wizard Dialog */}
        <Dialog 
          open={showWizard} 
          onClose={() => setShowWizard(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                SharePoint Configuration Wizard
              </Typography>
              <IconButton onClick={() => setShowWizard(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label, index) => (
                <Step key={label} completed={isWizardStepComplete(index)}>
                  <StepLabel
                    StepIconComponent={({ completed, active }) => (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {completed ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                      </Box>
                    )}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step Content */}
            {renderWizardStep()}
          </DialogContent>
          
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleWizardComplete}
                disabled={!testResult?.success}
                startIcon={<CheckIcon />}
              >
                Complete Setup
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isWizardStepComplete(activeStep)}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Start Wizard Button */}
        {!showWizard && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                SharePoint Not Configured
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Set up SharePoint integration to enable PDF storage for consent forms
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={startWizard}
                startIcon={<Settings />}
              >
                Start Setup Wizard
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  // Configuration overview (when configured)
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Storage sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Storage Configuration
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={startWizard}
          >
            Edit Configuration
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          SharePoint document library settings for PDF storage
        </Typography>
        
        {/* Status Chip */}
        <Chip
          icon={<CheckCircle />}
          label="Configured"
          color="success"
          variant="outlined"
        />
      </Box>

      {/* Configuration Overview */}
      <Grid container spacing={3}>
        {/* Site Configuration */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Site Configuration
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Storage />
                  </ListItemIcon>
                  <ListItemText
                    primary="SharePoint Site"
                    secondary={config.site_url}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudUpload />
                  </ListItemIcon>
                  <ListItemText
                    primary="Document Library"
                    secondary={config.document_library}
                  />
                </ListItem>
                {config.folder_path && (
                  <ListItem>
                    <ListItemIcon>
                      <Settings />
                    </ListItemIcon>
                    <ListItemText
                      primary="Default Folder"
                      secondary={config.folder_path}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Authentication Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Security sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Authentication Status
                </Typography>
              </Box>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Client ID"
                    secondary={`${config.client_id.substring(0, 8)}...`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Client Secret"
                    secondary="Securely stored (not displayed)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Authentication"
                    secondary="Configured"
                  />
                </ListItem>
                {config.tenant_id && (
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Tenant ID"
                      secondary={config.tenant_id}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <CheckCircle />}
                  onClick={testConnection}
                  disabled={testing}
                  sx={{ minWidth: 160 }}
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={startWizard}
                  sx={{ minWidth: 160 }}
                >
                  Edit Configuration
                </Button>
              </Box>

              {testResult && (
                <Alert 
                  severity={testResult.success ? 'success' : 'error'} 
                  sx={{ mt: 2 }}
                  icon={testResult.success ? <CheckCircle /> : <Error />}
                >
                  {testResult.message}
                </Alert>
              )}

              {saveSuccess && (
                <Alert 
                  severity="success" 
                  sx={{ mt: 2 }}
                  icon={<CheckCircle />}
                >
                  Configuration saved successfully!
                </Alert>
              )}

              {saveError && (
                <Alert 
                  severity="error" 
                  sx={{ mt: 2 }}
                  icon={<Error />}
                >
                  {saveError}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StorageConfig;